import React, { useEffect, useRef, memo } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import Input from "./../../../components/FormComponents/Input";
import { useAuth } from "../../../hooks/useAuth";
import { useHttp } from "../../../hooks/useHttp";
import { showError, showSuccess } from "../../../util/toast";
import { API_URLS, DEFAULT_HEADERS } from "../../../util/constants";
import { handleApiError } from "../../../util/errorHandler";
import { useNavigate } from "react-router";
import { Link } from "react-router-dom";

const authSchema = yup.object().shape({
  email: yup
    .string()
    .email("Você precisa inserir um e-mail válido!")
    .required("Insira um e-mail válido!"),
  password: yup
    .string()
    .required("Insira uma senha!")
    .min(8, "Insira uma senha válida!"),
});

const AuthForm = () => {
  const navigate = useNavigate();
  const isMountedRef = useRef(true);
  const isSubmittingRef = useRef(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: yupResolver(authSchema) });

  const { login } = useAuth();
  const { sendRequest } = useHttp();

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      // Reset ref ao desmontar
      isSubmittingRef.current = false;
    };
  }, []);

  const onSubmit = async (data) => {
    // Previne múltiplos submits
    if (isSubmittingRef.current) {
      return;
    }
    
    isSubmittingRef.current = true;
    
    try {
      const responseData = await sendRequest(
        API_URLS.LOGIN,
        "POST",
        JSON.stringify({
          email: data.email,
          password: data.password,
        }),
        DEFAULT_HEADERS.JSON_WITH_CORS
      );
      
      if (!isMountedRef.current) {
        isSubmittingRef.current = false;
        return;
      }
      
      if (responseData.success) {
        login(
          responseData.uuid,
          responseData.token,
          responseData.cashback,
          responseData.is_user_terra
        );

        // Exibe boas-vindas usando o primeiro nome quando disponível.
        // Se a API não trouxer nome, fazemos fallback a partir do e-mail.
        const fullName =
          responseData.user_name ||
          responseData.master_user_name ||
          responseData.nome ||
          responseData.name ||
          "";

        const firstName =
          (fullName || data.email || "")
            .split("@")[0]
            .replace(/[._-]+/g, " ")
            .trim()
            .split(/\s+/)[0] || "";

        const normalizedFirstName = firstName
          ? firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase()
          : "";

        if (normalizedFirstName) {
          showSuccess(`Seja bem-vindo(a), ${normalizedFirstName}!`);
        } else {
          showSuccess("Seja bem-vindo(a)!");
        }

        navigate("/produtos");
      } else {
        showError(responseData.message);
        isSubmittingRef.current = false;
      }
    } catch (error) {
      if (!isMountedRef.current) {
        isSubmittingRef.current = false;
        return;
      }
      handleApiError(error, { context: "AuthForm.onSubmit" });
      isSubmittingRef.current = false;
    }
  };
  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="col-md">
          <Input
            type="email"
            className={"input-item input-item-email mx-3 mb-30"}
            register={register}
            fieldName="email"
            placeholder="E-mail"
            errors={errors}
          />
        </div>
        <div className="col-md">
          <div>
            <Input
              className="input-item input-item-senha mx-3 mb-30"
              type="password"
              fieldName="password"
              register={register}
              placeholder="Senha"
              errors={errors}
            />
          </div>
        </div>
        <div className="col-md">
          <div className="btn-wrapper mt-0">
            <button
              className="btn theme-btn-1 btn-effect-1 text-uppercase mx-3"
              type="submit"
              disabled={isSubmitting}
              title="Enviar"
            >
              Enviar
            </button>
          </div>
        </div>
      </form>
      <div className="col-md m-3">
        <Link to="/redefinir-senha" title="Esqueci minha senha">
          Esqueci minha senha
        </Link>
      </div>
    </div>
  );
};
export default memo(AuthForm);
